import { FC, Fragment, useMemo } from 'react';
import { useParams, useLocation, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Types
import UserRoles from 'app/types/UserRoles';
import { AuditOperations } from 'app/store/Audit/Audit.types';
// Models
import { RootState } from 'app/store';
import { IDocument, IDocumentLink } from 'app/store/DMSDocuments/DMSDocuments.models';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { createAuditRecord } from 'app/store/Audit/Audit.async';
import { downloadDocument, processDocument } from 'app/store/DMSDocuments/DMSDocuments.async';
// Actions
import { AppUiDialogActions } from 'app/store/AppUIDialog/AppUIDialog.slice';
// Selectors
import { selectMyUser } from 'app/store/Users/Users.selectors';
import { selectLoading, selectDocumentEntity } from 'app/store/DMSDocuments/DMSDocuments.selectors';
import { selectPreferences, selectSort } from 'app/store/DMSBatches/DMSBatches.selectors';
// Mui
import {
  Theme, Box, ListItem, ListItemText,
  Typography, Tooltip,
  ListItemButton
} from '@mui/material';
// Icons
import {
  MoreVert as MoreVertIcon,
  Restore as RestoreIcon,
  Sync as SyncIcon,
  ErrorOutlineOutlined as ErrorOutlineOutlinedIcon
} from '@mui/icons-material';
// Dialogs
import DocumentFormDialog from 'app/dialogs/DocumentFormDialog';
import ConsentToExportDialog from 'app/dialogs/ConsentToExportDialog';
import ConfirmationDialog from 'app/dialogs/ConfirmationDialog';
import ReportFormDialog from 'app/pages/Staff/CaseDetailsPage/ReportsPage/ReportFormDialog';
// Components
import Labels from 'app/components/Labels';
import { IconButton } from 'app/components/Mui';
// Hooks
import useIsDocumentProcessAllowed from 'app/hooks/useIsDocumentProcessAllowed';
import useMenu from 'app/hooks/useMenu';
import useToggle from 'app/hooks/useToggle';
import useAccountStatus from 'app/hooks/useAccountStatus';
import useLongDateTime from 'app/hooks/useLongDateTime';
// Utilities
import { textToCapitalizeCase } from 'app/utilities/Utilities';
// 
import DocumentMoveDialog from './DocumentMoveDialog';
import DocumentDeleteDialog from './DocumentDeleteDialog';

type Props = {
  documentId: number;
};

const DocumentItem:FC<Props> = ({
  // Props
  documentId
}) => {
  const { t } = useTranslation();

  const { caseId } = useParams<{ caseId:string }>();
  const { pathname } = useLocation<{ pathname:string }>();

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const myUser = useAppSelector(selectMyUser);
  const documentEntity = useAppSelector((state:RootState) => selectDocumentEntity(state, { documentId })) as IDocument;
  const loading:boolean = useAppSelector(selectLoading);
  const preferences = useAppSelector(selectPreferences);
  const { sortBy } = useAppSelector(selectSort);

  const { showProcessMenuItem } = useIsDocumentProcessAllowed(documentEntity);

  const { isSuspended } = useAccountStatus();
  const { Menu, MenuItem, openMenu } = useMenu();
  const { open, toggle } = useToggle();
  const { open:openMoveDialog, toggle:toggleMoveDialog } = useToggle();
  const { open:openDeleteDialog, toggle:toggleDeleteDialog } = useToggle();
  const { open:openConsentToExportDialog, toggle:toggleConsentToExportDialog }  = useToggle();
  const { open:openConfirmationDialog, toggle:toggleConfirmationDialog } = useToggle();
  const { open:openReportFormDialog, toggle:toggleReportFormDialog } = useToggle();

  const links = useMemo(() => {
    if ( !documentEntity.links ) return {};
    return documentEntity.links.reduce((acc:Record<string, IDocumentLink>, cur:IDocumentLink) => {
      acc[cur.rel] = cur;
      return acc;
    }, {});
  }, [documentEntity.links]);

  const handleConfirm = async () => {
    try {
      await dispatch(processDocument(documentEntity.id)).unwrap();
      toggleConfirmationDialog();
    } catch(err){}
  }

  const handleConsetToExportConfirm = async () => {
    if ( openConsentToExportDialog ) toggleConsentToExportDialog();
    if ( !links.download ) return;

    try {
      await dispatch(downloadDocument({
        url: links.download.href,
        documentName: documentEntity.name
      })).unwrap();
      await dispatch(createAuditRecord({
        operation: AuditOperations.DocumentExport,
        data: {
          caseId: +caseId,
          userName: myUser?.name || '',
          fileName: documentEntity.name
        }
      }));
    } catch(error){
      console.log(error);
    }
  }

  const handleOpenLabelsDialog = () => {
    dispatch(AppUiDialogActions.showDialog({
      dialogName: 'DocumentLabelsDialog',
      dialogProps: {
        documentId: documentEntity.id,
        labels: documentEntity.labels,
      }
    }));
  }

  const status = !documentEntity.deleted ? documentEntity.processing.status : 'deleted';
  const isActiveLink = pathname.includes(`/documents/${documentEntity.id}`);
  const isActiveDocument = !documentEntity.deleted && documentEntity.processing.status === 'ready';

  const listItemProps = isActiveDocument ? {
    sx: {
      border: (theme:Theme) => isActiveLink ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
      color: 'initial',
      '&:hover': {
        bgcolor: isActiveLink ? 'initial' : 'rgba(0,0,0,0.025)',
        borderColor: (theme:Theme) => isActiveLink ? theme.palette.primary.main : 'rgba(0,0,0,0.025)'
      }
    }
  } : {
    sx: {
      opacity: ['pending', 'processing'].includes(status) ? 0.5 : 1,
      bgcolor: status === 'failed' ? 'rgba(255,0,0,0.15)' : 'initial',
    }
  };

  const handleClick = () => {
    const lastDocument = localStorage.getItem('shimlaw_ep:last_document') || `{}`;
    const parsed = JSON.parse(lastDocument);
    localStorage.setItem('shimlaw_ep:last_document', JSON.stringify({
      ...parsed,
      [caseId]: documentId
    }));
  }

  const [longDateTime] = useLongDateTime({
    dateValue: sortBy === 'modifiedOn'
      ? documentEntity.modifiedOn
      : documentEntity.createdOn,
    displayTime: true,
    local: true
  });
  const dateTimeTitle = sortBy === 'modifiedOn'
    ? t('labels.modifiedOn')
    : t('labels.createdOn');

  const isMyRoleAdmin = myUser?.role === UserRoles.Admin;
  const isCustomDocument = documentEntity.fileFormat === 'custom';
  const isDownloadMenuItemShown = !isCustomDocument;

  return (
    <Fragment>
      <ListItem
        {...listItemProps}
        data-scroll-id={`document-${documentEntity.id}`}
        data-document-status={status}
        disablePadding
        secondaryAction={
          isActiveDocument || status === 'failed' ? (
            <Fragment>
              <IconButton
                name={`Document dropdown ${documentEntity.id}`}
                aria-label="Document item dropdown button"
                onClick={openMenu}
                edge="end"
              ><MoreVertIcon /></IconButton>
              <Menu>
                <MenuItem
                  name={`Document labels ${documentEntity.id}`}
                  onClick={handleOpenLabelsDialog}
                >{t('labels.labels')}</MenuItem>
                {documentEntity.fileFormat === 'custom' ? (
                  <MenuItem
                    name={`Document edit ${documentEntity.id}`}
                    onClick={toggle}
                  >{t('buttons.edit')}</MenuItem>
                ) : null}
                {isCustomDocument ? (
                  <MenuItem
                    name={`Create report ${documentEntity.id}`}
                    onClick={toggleReportFormDialog}
                  >{t('buttons.createReport')}</MenuItem>
                ) : null}
                {isDownloadMenuItemShown ? (
                  <MenuItem
                    name={`Download ${documentEntity.id}`}
                    onClick={toggleConsentToExportDialog}
                  >{t('buttons.download')}</MenuItem>
                ) : null}
                <MenuItem
                  name={`Move document ${documentEntity.id}`}
                  onClick={toggleMoveDialog}
                >{t('buttons.move')}</MenuItem>
                {showProcessMenuItem ? (
                  <MenuItem
                    name={`Process document ${documentEntity.id}`}
                    onClick={toggleConfirmationDialog}
                    disabled={isSuspended}
                  >{t('buttons.process')}</MenuItem>
                ) : null}
                {isMyRoleAdmin && documentEntity.processing?.status === 'ready'  ? (
                  <MenuItem
                    name={`Delete document ${documentEntity.id}`}
                    sx={{
                      color: (theme:Theme) => theme.palette.error.main
                    }}
                    onClick={toggleDeleteDialog}
                  >{t('buttons.delete')}</MenuItem>
                ) : null}
              </Menu>
            </Fragment>
          ) : null
        }
      >
        {(() => {
          const content = (
            <Fragment>
              <ListItemText
                primary={
                  <Fragment>
                    <Typography
                      title={documentEntity.name}
                      display="block"
                      variant="subtitle2"
                      component="span"
                      noWrap
                    >{documentEntity.name}</Typography>
                    <Typography color="CaptionText" variant="body2">
                      <span title={dateTimeTitle}>
                        {documentEntity.numberOfPages ? (
                          <>
                            {documentEntity.numberOfPages} {t('labels.pages')}
                            {!preferences.groupByBatches && ` • ${longDateTime}`}
                          </>
                        ) : !preferences.groupByBatches ? longDateTime : null}
                      </span>
                    </Typography>
                    {documentEntity.labels && documentEntity.labels.length ? (
                      <Box sx={{ mt: 1 }}>
                        <Labels id={documentEntity.id} labels={documentEntity.labels} />
                      </Box>
                    ) : null}
                    {documentEntity.deleted ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
                        <Typography variant="body2" color="red"><b>{t('labels.deletedBy')}:</b>&nbsp;{documentEntity.deletedBy?.name}</Typography>
                        <Typography variant="body2"><b>{t('labels.message')}:</b>&nbsp;{documentEntity.message}</Typography>
                      </Box>
                    ) : null}
                  </Fragment>
                }
              />
              {['pending', 'processing', 'failed'].includes(status) && (
                <Box sx={{ width: 48, height: 40, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'end', pr: 2, mr: -2 }}>
                  <Tooltip title={textToCapitalizeCase(status)}>
                    {status === 'processing' ? <SyncIcon /> : status === 'pending' ? <RestoreIcon /> : <ErrorOutlineOutlinedIcon color="error" />}
                  </Tooltip>
                </Box>
              )}
            </Fragment>
          )

          const itemButtonProps = isActiveDocument ? {
            component: NavLink,
            to: `/admin/cases/${caseId}/documents/${documentEntity.id}`,
            onClick: handleClick
          } : {
            sx: {
              '&:hover': {
                bgcolor: 'initial',
                cursor: 'default'
              }
            }
          };

          return (
            <ListItemButton
              {...itemButtonProps}
            >{content}</ListItemButton>
          )
        })()}
      </ListItem>
      {open ? (
        <DocumentFormDialog
          open={open}
          onClose={toggle}
          document={documentEntity}
        />
      ) : null}
      {openMoveDialog ? (
        <DocumentMoveDialog
          open={openMoveDialog}
          onClose={toggleMoveDialog}
          documentEntity={documentEntity}
        />
      ) : null}
      {openDeleteDialog ? (
        <DocumentDeleteDialog
          open={openDeleteDialog}
          onClose={toggleDeleteDialog}
          documentId={documentId}
        />
      ) : null}
      {openConsentToExportDialog ? (
        <ConsentToExportDialog
          open={openConsentToExportDialog}
          onClose={toggleConsentToExportDialog}
          onConfirm={handleConsetToExportConfirm}
        />
      ) : null}
      {openConfirmationDialog ? (
        <ConfirmationDialog
          open={openConfirmationDialog}
          onClose={toggleConfirmationDialog}
          onConfirm={handleConfirm}
          title={t('components.filesSidebar.processTitle')}
          content={t('components.filesSidebar.processConfirmation', { documentName: documentEntity.name })}
          loading={loading}
        />
      ) : null}
      {openReportFormDialog ? (
        <ReportFormDialog
          open={openReportFormDialog}
          onClose={toggleReportFormDialog}
          documentId={documentEntity.id}
        />
      ) : null}
    </Fragment>
  )
}

export default DocumentItem;
